/** Navigation state; the route table is in routes.ts. */
import { untrack } from "svelte";
import { parse, setGroupSlugs, type Route } from "./routes.ts";

export * from "./routes.ts";

class Router {
  route = $state<Route>(parse(typeof location === "undefined" ? "/" : location.pathname));

  constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", () => (this.route = parse(location.pathname)));
    }
  }

  /** New list of groups: refresh their link names and re-read the current address. */
  setGroups(groups: readonly { id: string; name: string }[]) {
    setGroupSlugs(groups);
    if (typeof location === "undefined") return;
    // Only touch the route when it reads differently now, so nothing re-runs for no reason.
    const next = parse(location.pathname);
    if (JSON.stringify(next) !== JSON.stringify(untrack(() => this.route))) this.route = next;
  }

  go(path: string) {
    if (location.pathname + location.search !== path) history.pushState(null, "", path);
    this.route = parse(path);
    window.scrollTo({ top: 0 });
  }

  /** Changes the address without adding a history entry (for filling in the group). */
  replace(path: string) {
    if (location.pathname + location.search !== path) history.replaceState(null, "", path);
    this.route = parse(path);
  }
}

export const router = new Router();

/** Use on <a href> to navigate without a page load. */
export function link(node: HTMLAnchorElement) {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    const href = node.getAttribute("href");
    if (!href || !href.startsWith("/")) return;
    e.preventDefault();
    router.go(href);
  };
  node.addEventListener("click", onClick);
  return { destroy: () => node.removeEventListener("click", onClick) };
}
